import { useState, useCallback, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  alpha,
  Fade,
  IconButton,
  TextField,
  Stack,
  Switch,
  Alert,
} from '@mui/material';
import Add from '@mui/icons-material/Add';
import Delete from '@mui/icons-material/Delete';
import ArrowBack from '@mui/icons-material/ArrowBack';
import ArrowForward from '@mui/icons-material/ArrowForward';
import { WeeklySlot, DayConfig, OnboardingStepProps } from '../defs/types';

interface WeeklyOpeningTime {
  id?: number;
  dayOfWeek: number;
  startTime: string;
  endTime: string;
  isActive: boolean;
}

interface OpeningTimeStepProps extends OnboardingStepProps {
  onSave: (openingTimes: { dayOfWeek: number; slots: WeeklySlot[] }[]) => Promise<void>;
  existingOpeningTimes?: WeeklyOpeningTime[];
}

const PRIMARY_COLOR = '#2563eb';

const DAYS: DayConfig[] = [
  { value: 1, label: 'Lundi', shortLabel: 'Lundi' },
  { value: 2, label: 'Mardi', shortLabel: 'Mardi' },
  { value: 3, label: 'Mercredi', shortLabel: 'Mercredi' },
  { value: 4, label: 'Jeudi', shortLabel: 'Jeudi' },
  { value: 5, label: 'Vendredi', shortLabel: 'Vendredi' },
  { value: 6, label: 'Samedi', shortLabel: 'Samedi' },
  { value: 0, label: 'Dimanche', shortLabel: 'Dimanche' },
];

const OpeningTimeStep = ({
  onNext,
  onBack,
  isLoading,
  onSave,
  existingOpeningTimes = [],
}: OpeningTimeStepProps) => {
  const [weeklySlots, setWeeklySlots] = useState<Record<number, WeeklySlot[]>>(() => {
    const slots: Record<number, WeeklySlot[]> = {};
    DAYS.forEach((day) => {
      slots[day.value] = [
        { startTime: '09:00', endTime: '12:00' },
        { startTime: '13:00', endTime: '16:00' },
      ];
    });
    return slots;
  });
  const [activeDays, setActiveDays] = useState<Record<number, boolean>>(() => {
    const active: Record<number, boolean> = {};
    DAYS.forEach((day) => {
      active[day.value] = day.value >= 1 && day.value <= 5;
    });
    return active;
  });
  const [isSaving, setIsSaving] = useState(false);
  const [showError, setShowError] = useState(false);
  const [timeErrors, setTimeErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (existingOpeningTimes && existingOpeningTimes.length > 0) {
      const newWeeklySlots: Record<number, WeeklySlot[]> = {};
      const newActiveDays: Record<number, boolean> = {};

      DAYS.forEach((day) => {
        newActiveDays[day.value] = false;
        newWeeklySlots[day.value] = [];
      });

      existingOpeningTimes.forEach((openingTime) => {
        const dayOfWeek = openingTime.dayOfWeek;
        newActiveDays[dayOfWeek] = true;

        const startTime = openingTime.startTime.substring(0, 5);
        const endTime = openingTime.endTime.substring(0, 5);

        if (!newWeeklySlots[dayOfWeek]) {
          newWeeklySlots[dayOfWeek] = [];
        }
        newWeeklySlots[dayOfWeek].push({ startTime, endTime });
      });

      DAYS.forEach((day) => {
        if (!newActiveDays[day.value]) {
          newWeeklySlots[day.value] = [
            { startTime: '09:00', endTime: '12:00' },
            { startTime: '13:00', endTime: '16:00' },
          ];
        }
      });

      setWeeklySlots(newWeeklySlots);
      setActiveDays(newActiveDays);
    }
  }, [existingOpeningTimes]);

  const handleDayToggle = useCallback((dayOfWeek: number, checked: boolean) => {
    setActiveDays((prev) => ({ ...prev, [dayOfWeek]: checked }));
    setShowError(false);

    if (!checked) {
      setWeeklySlots((prev) => ({
        ...prev,
        [dayOfWeek]: [
          { startTime: '09:00', endTime: '12:00' },
          { startTime: '13:00', endTime: '16:00' },
        ],
      }));
      setTimeErrors((prev) => {
        const newErrors = { ...prev };
        Object.keys(newErrors).forEach((key) => {
          if (key.startsWith(`${dayOfWeek}-`)) {
            delete newErrors[key];
          }
        });
        return newErrors;
      });
    } else {
      setWeeklySlots((prev) => ({
        ...prev,
        [dayOfWeek]: [
          { startTime: '09:00', endTime: '12:00' },
          { startTime: '13:00', endTime: '16:00' },
        ],
      }));
    }
  }, []);

  const getNextSlotTime = useCallback(
    (dayOfWeek: number) => {
      const slots = weeklySlots[dayOfWeek] || [];
      if (slots.length === 0) {
        return { startTime: '09:00', endTime: '12:00' };
      }

      const lastSlot = slots[slots.length - 1];
      const lastEndTime = lastSlot.endTime;
      const [hours, minutes] = lastEndTime.split(':').map(Number);

      let nextStartHour = hours + 1;
      const nextStartMinute = minutes;

      if (nextStartHour >= 23) {
        nextStartHour = 23;
      }

      let nextEndHour = nextStartHour + 2;
      if (nextEndHour > 23) {
        nextEndHour = 23;
      }

      const startTime = `${String(nextStartHour).padStart(2, '0')}:${String(
        nextStartMinute
      ).padStart(2, '0')}`;
      const endTime = `${String(nextEndHour).padStart(2, '0')}:${String(nextStartMinute).padStart(
        2,
        '0'
      )}`;

      return { startTime, endTime };
    },
    [weeklySlots]
  );

  const handleAddSlot = useCallback(
    (dayOfWeek: number) => {
      const nextSlot = getNextSlotTime(dayOfWeek);
      setWeeklySlots((prev) => ({
        ...prev,
        [dayOfWeek]: [...(prev[dayOfWeek] || []), nextSlot],
      }));
    },
    [getNextSlotTime]
  );

  const handleRemoveSlot = useCallback((dayOfWeek: number, slotIndex: number) => {
    setWeeklySlots((prev) => ({
      ...prev,
      [dayOfWeek]: prev[dayOfWeek].filter((_, index) => index !== slotIndex),
    }));
    setTimeErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[`${dayOfWeek}-${slotIndex}`];
      return newErrors;
    });
  }, []);

  const validateTimeSlot = useCallback(
    (dayOfWeek: number, slotIndex: number, startTime: string, endTime: string) => {
      const errorKey = `${dayOfWeek}-${slotIndex}`;

      if (startTime >= endTime) {
        setTimeErrors((prev) => ({
          ...prev,
          [errorKey]: "L'heure de début doit être avant l'heure de fin",
        }));
        return false;
      }

      const slots = weeklySlots[dayOfWeek] || [];
      for (let i = 0; i < slots.length; i++) {
        if (i !== slotIndex) {
          const otherSlot = slots[i];
          if (
            (startTime >= otherSlot.startTime && startTime < otherSlot.endTime) ||
            (endTime > otherSlot.startTime && endTime <= otherSlot.endTime) ||
            (startTime <= otherSlot.startTime && endTime >= otherSlot.endTime)
          ) {
            setTimeErrors((prev) => ({
              ...prev,
              [errorKey]: 'Les créneaux horaires ne doivent pas se chevaucher',
            }));
            return false;
          }
        }
      }

      setTimeErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[errorKey];
        return newErrors;
      });
      return true;
    },
    [weeklySlots]
  );

  const handleSlotChange = useCallback(
    (dayOfWeek: number, slotIndex: number, field: 'startTime' | 'endTime', value: string) => {
      setWeeklySlots((prev) => {
        const slots = [...(prev[dayOfWeek] || [])];
        slots[slotIndex] = { ...slots[slotIndex], [field]: value };

        const slot = slots[slotIndex];
        validateTimeSlot(dayOfWeek, slotIndex, slot.startTime, slot.endTime);

        return { ...prev, [dayOfWeek]: slots };
      });
    },
    [validateTimeSlot]
  );

  const handleSave = async () => {
    const hasActiveDay = Object.values(activeDays).some((isActive) => isActive);

    if (!hasActiveDay) {
      setShowError(true);
      return;
    }

    if (Object.keys(timeErrors).length > 0) {
      return;
    }

    const openingTimes: { dayOfWeek: number; slots: WeeklySlot[] }[] = [];
    Object.entries(weeklySlots).forEach(([day, slots]) => {
      const dayOfWeek = parseInt(day);
      if (activeDays[dayOfWeek] && slots.length > 0) {
        const validSlots = slots.filter((slot) => {
          if (slot.startTime >= slot.endTime) {
            return false;
          }
          return true;
        });

        if (validSlots.length > 0) {
          openingTimes.push({ dayOfWeek, slots: validSlots });
        }
      }
    });

    if (openingTimes.length === 0) {
      setShowError(true);
      return;
    }

    setIsSaving(true);
    setShowError(false);

    try {
      await onSave(openingTimes);
      if (onNext) {
        onNext();
      }
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Fade in timeout={600}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          width: '100%',
          maxWidth: '800px',
          mx: 'auto',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5, width: '100%' }}>
          {onBack && (
            <IconButton onClick={onBack} sx={{ mr: 0 }} disabled={isSaving}>
              <ArrowBack />
            </IconButton>
          )}
          <Box sx={{ flex: 1, textAlign: 'center' }}>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontSize: '0.7rem', mb: 0.25 }}
            >
              Étape 2 sur 3
            </Typography>
            <Typography
              variant="h5"
              fontWeight={700}
              color="primary"
              sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}
            >
              J'indique mes horaires d'ouverture
            </Typography>
          </Box>
          <Box sx={{ width: 40 }} />
        </Box>

        {showError && (
          <Alert severity="error" sx={{ mb: 1, width: '100%', py: 0.5 }}>
            Veuillez activer au moins un jour avec des créneaux horaires valides.
          </Alert>
        )}

        <Box sx={{ width: '100%' }}>
          <Stack
            spacing={0}
            sx={{
              backgroundColor: '#f9fafb',
              borderRadius: 1.5,
              border: '1px solid #e5e7eb',
              maxHeight: 'calc(100vh - 280px)',
              overflowY: 'auto',
              boxShadow: '0 1px 2px rgba(0,0,0,0.08)',
              '&::-webkit-scrollbar': {
                width: '8px',
              },
              '&::-webkit-scrollbar-track': {
                backgroundColor: '#f1f1f1',
                borderRadius: '10px',
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: '#c1c1c1',
                borderRadius: '10px',
                '&:hover': {
                  backgroundColor: '#a8a8a8',
                },
              },
            }}
          >
            {DAYS.map((day) => (
              <Box
                key={day.value}
                sx={{
                  py: { xs: 0.75, sm: 1 },
                  px: { xs: 1.5, sm: 2 },
                  borderBottom: '1px solid #e5e7eb',
                  '&:last-child': { borderBottom: 'none' },
                  '&:hover': {
                    backgroundColor: alpha('#2563eb', 0.02),
                  },
                  transition: 'background-color 0.2s',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    alignItems: { xs: 'flex-start', sm: 'center' },
                    gap: { xs: 0.75, sm: 1.5 },
                  }}
                >
                  <Box sx={{ width: { xs: '100%', sm: '130px' } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', height: 32 }}>
                      <Switch
                        checked={activeDays[day.value] || false}
                        onChange={(e) => handleDayToggle(day.value, e.target.checked)}
                        size="small"
                        sx={{
                          '& .MuiSwitch-switchBase.Mui-checked': {
                            color: PRIMARY_COLOR,
                          },
                          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                            backgroundColor: PRIMARY_COLOR,
                          },
                        }}
                      />
                      <Typography
                        sx={{
                          ml: 0.75,
                          fontWeight: 500,
                          color: activeDays[day.value] ? '#111827' : '#9ca3af',
                          fontSize: { xs: '0.8rem', sm: '0.85rem' },
                        }}
                      >
                        {day.shortLabel}
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{ flex: 1, width: { xs: '100%', sm: 'auto' } }}>
                    {activeDays[day.value] && (
                      <Stack spacing={0.5}>
                        {(weeklySlots[day.value] || []).map((slot, index) => {
                          const errorKey = `${day.value}-${index}`;
                          const hasError = !!timeErrors[errorKey];
                          return (
                            <Box key={index}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                <TextField
                                  type="time"
                                  size="small"
                                  value={slot.startTime}
                                  onChange={(e) =>
                                    handleSlotChange(day.value, index, 'startTime', e.target.value)
                                  }
                                  error={hasError}
                                  sx={{
                                    '& .MuiOutlinedInput-root': {
                                      height: 36,
                                      width: 100,
                                      paddingRight: 0.5,
                                      backgroundColor: '#ffffff',
                                      '& fieldset': {
                                        borderColor: hasError ? '#ef4444' : '#e5e7eb',
                                      },
                                      '&.Mui-focused fieldset': {
                                        borderColor: hasError ? '#ef4444' : PRIMARY_COLOR,
                                        borderWidth: 1,
                                      },
                                    },
                                    '& input': {
                                      fontSize: { xs: '0.7rem', sm: '0.8rem' },
                                      padding: '6px 8px',
                                    },
                                  }}
                                />
                                <Typography sx={{ color: '#6b7280', fontSize: '0.8rem' }}>
                                  -
                                </Typography>
                                <TextField
                                  type="time"
                                  size="small"
                                  value={slot.endTime}
                                  onChange={(e) =>
                                    handleSlotChange(day.value, index, 'endTime', e.target.value)
                                  }
                                  error={hasError}
                                  sx={{
                                    '& .MuiOutlinedInput-root': {
                                      backgroundColor: '#ffffff',
                                      height: 36,
                                      width: 100,
                                      paddingRight: 0.5,
                                      '& fieldset': {
                                        borderColor: hasError ? '#ef4444' : '#e5e7eb',
                                      },
                                      '&.Mui-focused fieldset': {
                                        borderColor: hasError ? '#ef4444' : PRIMARY_COLOR,
                                        borderWidth: 1,
                                      },
                                    },
                                    '& input': {
                                      fontSize: { xs: '0.7rem', sm: '0.8rem' },
                                      padding: '6px 8px',
                                    },
                                  }}
                                />

                                <Box sx={{ display: 'flex', gap: 0.25, ml: 'auto' }}>
                                  {index === (weeklySlots[day.value] || []).length - 1 && (
                                    <IconButton
                                      size="small"
                                      onClick={() => handleAddSlot(day.value)}
                                      sx={{
                                        color: PRIMARY_COLOR,
                                        backgroundColor: alpha(PRIMARY_COLOR, 0.08),
                                        '&:hover': {
                                          backgroundColor: alpha(PRIMARY_COLOR, 0.15),
                                        },
                                        width: 26,
                                        height: 26,
                                      }}
                                    >
                                      <Add sx={{ fontSize: 16 }} />
                                    </IconButton>
                                  )}
                                  {(weeklySlots[day.value] || []).length > 1 && (
                                    <IconButton
                                      size="small"
                                      onClick={() => handleRemoveSlot(day.value, index)}
                                      sx={{
                                        color: '#6b7280',
                                        '&:hover': {
                                          backgroundColor: alpha('#ef4444', 0.08),
                                          color: '#ef4444',
                                        },
                                        width: 26,
                                        height: 26,
                                      }}
                                    >
                                      <Delete sx={{ fontSize: 16 }} />
                                    </IconButton>
                                  )}
                                </Box>
                              </Box>
                              {hasError && (
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: '#ef4444',
                                    fontSize: '0.7rem',
                                    mt: 0.5,
                                    display: 'block',
                                  }}
                                >
                                  {timeErrors[errorKey]}
                                </Typography>
                              )}
                            </Box>
                          );
                        })}
                      </Stack>
                    )}
                  </Box>
                </Box>
              </Box>
            ))}
          </Stack>
        </Box>

        <Box
          sx={{
            mt: 2,
            display: 'flex',
            gap: 2,
            justifyContent: 'center',
            width: '100%',
            px: { xs: 2, sm: 0 },
          }}
        >
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={isSaving || isLoading || Object.keys(timeErrors).length > 0}
            startIcon={
              isSaving ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <ArrowForward sx={{ fontSize: 18 }} />
              )
            }
            sx={{
              backgroundColor: PRIMARY_COLOR,
              color: '#fff',
              py: 1,
              fontSize: '0.9rem',
              fontWeight: 600,
              textTransform: 'none',
              borderRadius: 1.5,
              flex: 1,
              maxWidth: '180px',
              boxShadow: '0 2px 8px rgba(37, 99, 235, 0.2)',
              '&:hover': {
                backgroundColor: '#1d4ed8',
                boxShadow: `0 4px 16px ${alpha(PRIMARY_COLOR, 0.3)}`,
              },
              '&.Mui-disabled': {
                backgroundColor: alpha(PRIMARY_COLOR, 0.6),
                color: '#fff',
              },
            }}
          >
            {isSaving ? 'Enregistrement...' : 'Continuer'}
          </Button>
        </Box>
      </Box>
    </Fade>
  );
};

export default OpeningTimeStep;
